import React, { useEffect, useState } from "react";
import "./InstallPWA.css";

const IOS_STORAGE_PREFIX = "iosModalDismissed";
const MODAL_DELAY_MS = 2000;
const DEFAULT_PORTAL_NAME = "Portal Pacientes Amprosa";

const getPortalName = () => DEFAULT_PORTAL_NAME;

const getIOSStorageKey = () => {
  const normalizedPortalName = getPortalName().replace(/[^a-z0-9]/gi, "");
  return `${IOS_STORAGE_PREFIX}${normalizedPortalName}`;
};

const InstallPWA = () => {
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [portalName, setPortalName] = useState(DEFAULT_PORTAL_NAME);
  const [platform, setPlatform] = useState({
    isAndroid: false,
    isIOS: false,
    isIOSChrome: false,
    isIOSSafari: false,
    isStandalone: false,
    isSecureContext: window.isSecureContext,
  });

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroidDevice = /Android/i.test(userAgent);
    const isIOSChrome = /CriOS/i.test(userAgent);
    const isIOSSafari =
      isIOSDevice && /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS/i.test(userAgent);
    const isStandaloneMode =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;

    setPortalName(getPortalName());
    setPlatform({
      isAndroid: isAndroidDevice,
      isIOS: isIOSDevice,
      isIOSChrome,
      isIOSSafari,
      isStandalone: isStandaloneMode,
      isSecureContext: window.isSecureContext,
    });

    if (isStandaloneMode) {
      return undefined;
    }

    let fallbackTimer;

    if (isIOSDevice) {
      if (!localStorage.getItem(getIOSStorageKey())) {
        fallbackTimer = setTimeout(() => setShowModal(true), MODAL_DELAY_MS);
      }

      return () => {
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
        }
      };
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowModal(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setShowModal(false);
      setPlatform((currentPlatform) => ({
        ...currentPlatform,
        isStandalone: true,
      }));
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    fallbackTimer = setTimeout(() => {
      if (isAndroidDevice) {
        setShowModal(true);
      }
    }, MODAL_DELAY_MS);

    return () => {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);

    if (platform.isIOS) {
      localStorage.setItem(getIOSStorageKey(), "true");
    }
  };

  if (platform.isStandalone || !showModal) return null;

  const showInstallButton = Boolean(deferredPrompt);
  const iosBrowserLabel = (() => {
    if (platform.isIOSChrome) {
      return "Chrome";
    }

    if (platform.isIOSSafari) {
      return "Safari";
    }

    return "Safari o Chrome";
  })();

  return (
    <div className="pwa-modal-overlay">
      <div className="pwa-modal">
        <h2>{showInstallButton ? "Crear acceso directo" : `Crear ${portalName}`}</h2>
        {platform.isIOS ? (
          <div className="ios-instructions">
            <p>Para crear el acceso directo de {portalName} en tu iPhone desde {iosBrowserLabel}:</p>
            <ol>
              <li>Tocá Compartir en la barra del navegador.</li>
              <li>Elegí Crear acceso directo en pantalla de inicio.</li>
              <li>Tocá Crear para confirmar.</li>
            </ol>
            <p>iOS no permite crear el acceso directo automáticamente desde la web, por eso este paso es manual.</p>
          </div>
        ) : platform.isAndroid && !showInstallButton ? (
          <div className="android-instructions">
            <p>Si tu navegador no mostró la opción automática, podés crear el acceso directo de {portalName} manualmente:</p>
            <ol>
              <li>Abrí el menú de tres puntos del navegador.</li>
              <li>Elegí Instalar o Crear acceso directo en pantalla principal.</li>
              <li>Confirmá la creación del acceso directo.</li>
            </ol>
            {!platform.isSecureContext ? (
              <p className="pwa-note">
                En localhost o en http://IP:puerto puede verse este modal, pero crear el acceso directo realmente requiere HTTPS o
                un contexto seguro.
              </p>
            ) : null}
          </div>
        ) : (
          <p>Creá el acceso directo de {portalName} para entrar más rápido desde tu dispositivo.</p>
        )}
        <div className="pwa-modal-buttons">
          {showInstallButton ? (
            <button onClick={handleInstall} className="install-button">
              Instalar
            </button>
          ) : null}
          <button onClick={handleModalClose} className="close-button">
            {platform.isIOS ? "Entendido" : "Ahora no"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
